/**
 * SystemProductPicker — the search-first product-connection control
 * (T12.1.8.2 · upgraded T27.2.7.10).
 *
 * The FOREGROUND product-preference surface on the HouseholdProduct detail
 * screen ("Products you want"): a household searches the catalog
 * (too large to browse as a list) and marks several products as `preferred` and
 * several as `substitute`, or firmly `exclude`s a product from suggestions. This
 * is the primary signal the suggestion algorithm matches on (ranking is T12.5);
 * the brand-preference control (HouseholdProductSettings) is kept but secondary.
 *
 * Built to the planning-shopping prototype `#s-settings` (`.setgroup.sp-picker`):
 *   - one flat bordered group (no nested Card / List); the summary is just the
 *     title + a quiet "What's this?" toggle (`.whatsbtn`) that reveals the
 *     group's explanation in place (hidden by default — the labels carry
 *     first-time navigation, so the surface reads light);
 *   - the current pins split into `Preferred` / `Substitutes` `.subhead` groups
 *     of `.pinrow`s (name + cadence meta · `.rolebtn` flip · bare ✕ remove);
 *   - an **Auto-matched** block (`.automatch`) — the system-linked products the
 *     auto-matcher created, shown with **Prefer / Avoid** (no bare Remove). Either
 *     action flips the link to a deliberate user stance, so it leaves the set
 *     (HP↔SP link UI model). A bare Remove isn't offered — the auto-matcher would
 *     just re-link it; Avoid is the durable keep-out.
 *   - "Add product preference" opens a BOUNDED search SHEET (`#choose-sheet`),
 *     pre-filled with the household product's name and run — never an inline box
 *     that pushes the surface off-screen. The field stays editable to broaden;
 *     a "no matches" state hosts the contextual reporter.
 *   - search results as `.presult` rows of `.miniadd` pills
 *     (`.pref` solid / `.sub` muted / Don't-suggest danger);
 *   - excluded products as `.tag.excl` chips (🚫 + danger), matching the brand
 *     grain's firm-control look.
 *
 * All preferences are SOFT and advisory — never a hard filter. Styles flow from
 * tokens (CP5): no hex, no raw px.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  useHouseholdProduct,
  useHouseholdProductPreferences,
  useSearchSystemProducts,
  useLinkSystemProductPreference,
  useUnlinkSystemProductPreference,
  useSetSystemProductPreference,
} from '@shopista/shared/api/hooks';
import { apiErrorMessage } from '@shopista/shared/api';
import type { SystemProductSearchParams, HouseholdProductPreferences } from '@shopista/shared/types';
import { Spinner } from '../../components';
import { Modal } from '../../components/Modal';
import {
  setgroupStyle,
  detailsSummaryStyle,
  sumttlStyle,
  hintStyle,
  whatsBtnStyle,
  subheadStyle,
  subnoteStyle,
  pinrowStyle,
  pinrowMetaStyle,
  pinrowNmStyle,
  pinrowCadStyle,
  atstoreStyle,
  rolebtnStyle,
  rolebtnAvoidStyle,
  pinrowXStyle,
  setsearchStyle,
  setsearchInputStyle,
  presultboxStyle,
  presultStyle,
  presultMetaStyle,
  presultNmStyle,
  presultCadStyle,
  presultRbStyle,
  miniaddPrefStyle,
  miniaddSubStyle,
  miniaddExclStyle,
  tagsRowStyle,
  tagExclStyle,
  tagXStyle,
  toggleRowStyle,
  errorStyle,
  mutedStyle,
  connectedBadgeStyle,
  addprefStyle,
  addprefPlusStyle,
  automatchStyle,
  automatchSummaryStyle,
  amcountStyle,
  amtagStyle,
} from './settingsStyles';
import { ProductSearchReporter } from '../report-problems';

/**
 * Group items by category_label. Items with null/undefined category_label
 * fall into the "Other" bucket, which is always present (never hidden).
 * Returns a Map where the "Other" bucket is always last.
 */
type Grouped<T> = Map<string, T[]>;

function groupByCategory<T extends { category_label?: string | null | undefined }>(
  items: T[]
): Grouped<T> {
  const groups: Grouped<T> = new Map();
  const other: T[] = [];

  for (const item of items) {
    if (item.category_label) {
      if (!groups.has(item.category_label)) {
        groups.set(item.category_label, []);
      }
      groups.get(item.category_label)!.push(item);
    } else {
      other.push(item);
    }
  }

  // Always add "Other" at the end, even if empty
  groups.set('Other', other);

  return groups;
}

export function SystemProductPicker({ productId }: { productId: string }): JSX.Element {
  const prefsQuery = useHouseholdProductPreferences(productId);
  const productQuery = useHouseholdProduct(productId);

  const [sheetOpen, setSheetOpen] = useState(false);
  // The explanation is hidden until the "What's this?" toggle is tapped — the
  // labels carry first-time navigation, so the surface reads light (the
  // prototype's `.hint.help` revealed by `toggleHint`).
  const [helpOpen, setHelpOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableOnly, setAvailableOnly] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const productName = productQuery.data?.display_name ?? '';

  const searchParams: SystemProductSearchParams = useMemo(
    () => ({
      query: searchTerm.trim(),
      available_only: availableOnly || undefined,
      limit: 20,
      offset: 0,
    }),
    [searchTerm, availableOnly]
  );

  // Only fetch while the sheet is open with a non-empty term — the surface is
  // quiet until the household opens the bounded search.
  const searchQuery = useSearchSystemProducts(
    searchParams,
    sheetOpen && searchTerm.trim().length > 0
  );

  const link = useLinkSystemProductPreference();
  const unlink = useUnlinkSystemProductPreference();
  const setPreference = useSetSystemProductPreference();

  const pins = prefsQuery.data?.systemProducts ?? [];
  const autoMatched = prefsQuery.data?.autoMatched ?? [];
  const pinnedIds = useMemo(() => new Set(pins.map((p) => p.systemproduct_id)), [pins]);

  const run = async (fn: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Could not update the product connection'));
    }
  };

  // Open the bounded sheet pre-filled with the household product's name and run
  // the search, so opening it on "Milk" shows milk products immediately instead
  // of a blank "type to search" box. The field stays editable to broaden.
  const openSheet = () => {
    setSearchTerm(productName);
    setSheetOpen(true);
  };
  const closeSheet = () => setSheetOpen(false);

  // If the product name resolves after the sheet was opened (race on first
  // mount), seed the still-empty field once.
  useEffect(() => {
    if (sheetOpen && searchTerm === '' && productName !== '') {
      setSearchTerm(productName);
    }
  }, [sheetOpen, productName, searchTerm]);

  const preferredPins = pins.filter((p) => p.preference === 'preferred');
  const substitutePins = pins.filter((p) => p.preference === 'substitute');
  const excludedPins = pins.filter((p) => p.preference === 'exclude');
  const results = searchQuery.data?.items ?? [];

  // Group pins by category for rendering
  const preferredGroups = useMemo(() => groupByCategory(preferredPins), [preferredPins]);
  const substituteGroups = useMemo(() => groupByCategory(substitutePins), [substitutePins]);
  const autoMatchedGroups = useMemo(() => groupByCategory(autoMatched), [autoMatched]);

  return (
    <details style={setgroupStyle} data-testid="sp-picker" data-section="products" open>
      <summary style={detailsSummaryStyle}>
        <span style={sumttlStyle}>Product preferences</span>
        <button
          type="button"
          style={whatsBtnStyle}
          aria-label="What's this?"
          aria-expanded={helpOpen}
          // Toggle the explanation in place. Stop the click from reaching the
          // <summary> (which would fold the open group) — mirrors the
          // prototype's `event.preventDefault();event.stopPropagation()`.
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setHelpOpen((v) => !v);
          }}
          data-testid="sp-picker-whats-this"
        >
          What&apos;s this?
        </button>
      </summary>
      {helpOpen && (
        <p style={hintStyle} data-testid="sp-picker-help">
          The exact products you buy — one line per product, spanning every package
          size. We follow their offers across your stores, even as individual sales
          come and go.
        </p>
      )}

      {actionError && (
        <div role="alert" style={errorStyle} data-testid="sp-picker-error">
          {actionError}
        </div>
      )}

      {/* --- Add product preference — opens the bounded search sheet --------- */}
      <button
        type="button"
        style={addprefStyle}
        onClick={openSheet}
        data-testid="sp-picker-add"
      >
        <span aria-hidden="true" style={addprefPlusStyle}>
          +
        </span>{' '}
        Add product preference
      </button>

      {/* --- Preferred / Substitutes (side-by-side at desktop width) -------- */}
      <div data-testid="sp-picker-pins" className="hp-sp-cols">
        <div>
          <div style={subheadStyle}>Preferred</div>
          {preferredPins.length === 0 ? (
            <span style={mutedStyle} data-testid="sp-picker-preferred-empty">
              Nothing preferred yet — add some above.
            </span>
          ) : (
            Array.from(preferredGroups.entries()).map(([category, items]) => {
              // Skip the "Other" group if it has no items
              if (category === 'Other' && items.length === 0) {
                return null;
              }
              return (
                <div key={category} data-testid={`sp-picker-preferred-group-${category}`}>
                  {items.length > 0 && <div style={subheadStyle}>{category}</div>}
                  {items.map((p) => (
                    <div
                      style={pinrowStyle}
                      key={p.systemproduct_id}
                      data-testid={`sp-picker-pin-${p.systemproduct_id}`}
                    >
                      <div style={pinrowMetaStyle}>
                        <div style={pinrowNmStyle}>{p.name}</div>
                      </div>
                      <span
                        style={mutedStyle}
                        data-testid={`sp-picker-pin-${p.systemproduct_id}-preference`}
                        hidden
                      >
                        {p.preference}
                      </span>
                      <button
                        type="button"
                        style={rolebtnStyle}
                        onClick={() =>
                          void run(() =>
                            setPreference.mutateAsync({
                              productId,
                              systemProductId: p.systemproduct_id,
                              preference: 'substitute',
                            })
                          )
                        }
                        data-testid={`sp-picker-pin-${p.systemproduct_id}-toggle-preference`}
                      >
                        Make substitute
                      </button>
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label="Remove"
                        style={pinrowXStyle}
                        onClick={() =>
                          void run(() =>
                            unlink.mutateAsync({ productId, systemProductId: p.systemproduct_id })
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            void run(() =>
                              unlink.mutateAsync({ productId, systemProductId: p.systemproduct_id })
                            );
                          }
                        }}
                        data-testid={`sp-picker-pin-${p.systemproduct_id}-remove`}
                      >
                        ✕
                      </span>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>

        {/* --- Substitutes -------------------------------------------------- */}
        <div>
          <div style={subheadStyle}>Substitutes</div>
          <div style={subnoteStyle}>Fine when your preferred isn&apos;t around.</div>
          {substitutePins.length === 0 ? (
            <span style={mutedStyle} data-testid="sp-picker-substitute-empty">
              No substitutes yet.
            </span>
          ) : (
            Array.from(substituteGroups.entries()).map(([category, items]) => {
              // Skip the "Other" group if it has no items
              if (category === 'Other' && items.length === 0) {
                return null;
              }
              return (
                <div key={category} data-testid={`sp-picker-substitute-group-${category}`}>
                  {items.length > 0 && <div style={subheadStyle}>{category}</div>}
                  {items.map((p) => (
                    <div
                      style={pinrowStyle}
                      key={p.systemproduct_id}
                      data-testid={`sp-picker-pin-${p.systemproduct_id}`}
                    >
                      <div style={pinrowMetaStyle}>
                        <div style={pinrowNmStyle}>{p.name}</div>
                      </div>
                      <span
                        style={mutedStyle}
                        data-testid={`sp-picker-pin-${p.systemproduct_id}-preference`}
                        hidden
                      >
                        {p.preference}
                      </span>
                      <button
                        type="button"
                        style={rolebtnStyle}
                        onClick={() =>
                          void run(() =>
                            setPreference.mutateAsync({
                              productId,
                              systemProductId: p.systemproduct_id,
                              preference: 'preferred',
                            })
                          )
                        }
                        data-testid={`sp-picker-pin-${p.systemproduct_id}-toggle-preference`}
                      >
                        Make preferred
                      </button>
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label="Remove"
                        style={pinrowXStyle}
                        onClick={() =>
                          void run(() =>
                            unlink.mutateAsync({ productId, systemProductId: p.systemproduct_id })
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            void run(() =>
                              unlink.mutateAsync({ productId, systemProductId: p.systemproduct_id })
                            );
                          }
                        }}
                        data-testid={`sp-picker-pin-${p.systemproduct_id}-remove`}
                      >
                        ✕
                      </span>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* --- Don't suggest (excluded products) — `.tag.excl` chips, matching
          the brand grain's firm-control look. -------------------------------- */}
      <div data-testid="sp-picker-exclude">
        <div style={subheadStyle}>Don&apos;t suggest</div>
        <div style={subnoteStyle}>Kept out of suggestions — still findable by search.</div>
        {excludedPins.length === 0 ? (
          <span style={mutedStyle} data-testid="sp-picker-exclude-empty">
            Nothing held back yet — choose &ldquo;Avoid&rdquo; on an auto-match or
            &ldquo;Don&apos;t suggest&rdquo; on a search result.
          </span>
        ) : (
          <div style={tagsRowStyle} data-testid="sp-picker-exclude-list">
            {excludedPins.map((p) => (
              <span
                style={tagExclStyle}
                key={p.systemproduct_id}
                data-testid={`sp-picker-exclude-${p.systemproduct_id}`}
              >
                🚫 {p.name}
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Remove"
                  style={tagXStyle}
                  onClick={() =>
                    void run(() =>
                      unlink.mutateAsync({ productId, systemProductId: p.systemproduct_id })
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      void run(() =>
                        unlink.mutateAsync({ productId, systemProductId: p.systemproduct_id })
                      );
                    }
                  }}
                  data-testid={`sp-picker-exclude-${p.systemproduct_id}-remove`}
                >
                  ✕
                </span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* --- Auto-matched (system-linked products) — Prefer / Avoid ---------
          The system-provenance links the auto-matcher created so deals resolve.
          Prefer → manual Preferred; Avoid → manual Don't suggest. Either flips the
          link out of the automatic set (provenance system → user). No bare Remove
          (the matcher would re-link it; Avoid is the durable keep-out). */}
      {autoMatched.length > 0 && (
        <details style={automatchStyle} data-testid="sp-picker-automatch">
          <summary style={automatchSummaryStyle}>
            <span>Auto-matched</span>
            <span style={amcountStyle} data-testid="sp-picker-automatch-count">
              {autoMatched.length}
            </span>
          </summary>
          <p style={{ ...hintStyle, margin: `var(--space-2) 0 var(--space-2)` }}>
            Automatically linked so we can match this product. Promote the ones you
            actually buy, or avoid any that don&apos;t belong — either moves it out
            of automatic.
          </p>
          <div>
            {Array.from(autoMatchedGroups.entries()).map(([category, items]) => {
              // Skip the "Other" group if it has no items
              if (category === 'Other' && items.length === 0) {
                return null;
              }
              return (
                <div key={category} data-testid={`sp-picker-automatch-group-${category}`}>
                  {items.length > 0 && <div style={subheadStyle}>{category}</div>}
                  {items.map((m) => (
                    <div
                      style={pinrowStyle}
                      key={m.systemproduct_id}
                      data-testid={`sp-picker-automatch-${m.systemproduct_id}`}
                    >
                      <div style={pinrowMetaStyle}>
                        <div style={pinrowNmStyle}>
                          {m.name}
                          <span style={amtagStyle}>auto</span>
                        </div>
                        {m.brand_label && <div style={pinrowCadStyle}>{m.brand_label}</div>}
                      </div>
                      <button
                        type="button"
                        style={rolebtnStyle}
                        onClick={() =>
                          void run(() =>
                            // Prefer → a deliberate Preferred stance. linkSystemProduct
                            // with provenance=user upgrades the existing system row.
                            link.mutateAsync({
                              productId,
                              systemProductId: m.systemproduct_id,
                              preference: 'preferred',
                            })
                          )
                        }
                        data-testid={`sp-picker-automatch-${m.systemproduct_id}-prefer`}
                      >
                        Prefer
                      </button>
                      <button
                        type="button"
                        style={rolebtnAvoidStyle}
                        onClick={() =>
                          void run(() =>
                            // Avoid → a deliberate Don't-suggest stance.
                            link.mutateAsync({
                              productId,
                              systemProductId: m.systemproduct_id,
                              preference: 'exclude',
                            })
                          )
                        }
                        data-testid={`sp-picker-automatch-${m.systemproduct_id}-avoid`}
                      >
                        Avoid
                      </button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </details>
      )}

      {/* --- Bounded search sheet (pre-filled, editable) -------------------- */}
      <Modal
        open={sheetOpen}
        onClose={closeSheet}
        variant="sheet"
        title="Add product preference"
        ariaLabel="Add product preference"
      >
        <div data-testid="sp-picker-search">
          <p style={{ ...hintStyle, marginTop: 0 }}>
            {productName
              ? `Matches for “${productName}” — pick the ones you actually buy.`
              : 'Search by name and add the ones you actually buy.'}
          </p>
          <div className="field-focus-ring" style={setsearchStyle}>
            <span aria-hidden="true" style={{ color: 'var(--color-text-muted)' }}>
              🔍
            </span>
            <input
              style={setsearchInputStyle}
              aria-label="Search products"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search products to add…"
              data-testid="sp-picker-search-input"
              autoFocus
            />
          </div>
          <label style={toggleRowStyle}>
            <input
              type="checkbox"
              checked={availableOnly}
              onChange={(e) => setAvailableOnly(e.target.checked)}
              data-testid="sp-picker-available-toggle"
            />
            <span style={mutedStyle}>Only show products available at my stores</span>
          </label>

          {searchTerm.trim().length === 0 ? (
            <span style={mutedStyle} data-testid="sp-picker-search-hint">
              Type to search the product catalog.
            </span>
          ) : searchQuery.isLoading ? (
            <div data-testid="sp-picker-search-loading">
              <Spinner />
            </div>
          ) : searchQuery.isError ? (
            <span style={errorStyle} data-testid="sp-picker-search-error">
              Could not search products.
            </span>
          ) : results.length === 0 ? (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}
              data-testid="sp-picker-results-empty"
            >
              <span style={mutedStyle}>No matching products.</span>
              <ProductSearchReporter searchTerm={searchTerm} surface="catalog" />
            </div>
          ) : (
            <div style={presultboxStyle} data-testid="sp-picker-results">
              {results.map((r) => {
                const alreadyPinned = pinnedIds.has(r.id);
                return (
                  <div style={presultStyle} key={r.id} data-testid={`sp-picker-result-${r.id}`}>
                    <div style={presultMetaStyle}>
                      <div style={presultNmStyle}>{r.name}</div>
                      <div style={presultCadStyle}>
                        {r.brand_name && <span>{r.brand_name}</span>}
                        {r.brand_name && r.available_at_my_stores && <span> · </span>}
                        {r.available_at_my_stores && (
                          <span
                            style={atstoreStyle}
                            data-testid={`sp-picker-result-${r.id}-available`}
                          >
                            at your stores
                          </span>
                        )}
                      </div>
                    </div>
                    {alreadyPinned ? (
                      <span
                        style={connectedBadgeStyle}
                        data-testid={`sp-picker-result-${r.id}-pinned`}
                      >
                        connected
                      </span>
                    ) : (
                      <div style={presultRbStyle}>
                        <button
                          type="button"
                          style={miniaddPrefStyle}
                          onClick={() =>
                            void run(() =>
                              link.mutateAsync({
                                productId,
                                systemProductId: r.id,
                                preference: 'preferred',
                              })
                            )
                          }
                          data-testid={`sp-picker-result-${r.id}-mark-preferred`}
                        >
                          Preferred
                        </button>
                        <button
                          type="button"
                          style={miniaddSubStyle}
                          onClick={() =>
                            void run(() =>
                              link.mutateAsync({
                                productId,
                                systemProductId: r.id,
                                preference: 'substitute',
                              })
                            )
                          }
                          data-testid={`sp-picker-result-${r.id}-mark-substitute`}
                        >
                          Substitute
                        </button>
                        <button
                          type="button"
                          style={miniaddExclStyle}
                          onClick={() =>
                            void run(() =>
                              link.mutateAsync({
                                productId,
                                systemProductId: r.id,
                                preference: 'exclude',
                              })
                            )
                          }
                          data-testid={`sp-picker-result-${r.id}-mark-exclude`}
                        >
                          🚫 Don&apos;t suggest
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal>
    </details>
  );
}
